import React, { useMemo, useState } from 'react';
import scaleLinear from '@visx/scale/lib/scales/linear';
import { beeswarm } from 'd3-beeswarm';
import { useThemeColors } from '../../hooks/useThemeColors';
import ChartA11y from './ChartA11y';

const isDev = process.env.NODE_ENV !== 'production';
const DEFAULT_THRESHOLDS = [20, 40, 60, 80];
const DEFAULT_MAX_DOTS = 200;
const DOT_RADIUS = 4;

function bandIndex(score, thresholds) {
  for (let i = 0; i < thresholds.length; i++) {
    if (score < thresholds[i]) return i;
  }
  return thresholds.length;
}

function colorForBand(idx, colors) {
  // Higher band = worse. PRD-51 polarity: higher score = riskier.
  // Map ascending bands to: healthy → low → medium → high → critical
  const map = [
    colors.severity.low,
    colors.severity.info,
    colors.severity.medium,
    colors.severity.high,
    colors.severity.critical,
  ];
  return map[Math.min(idx, map.length - 1)];
}

export default function RiskBeeswarm({
  data,
  width = 800,
  height = 120,
  thresholds = DEFAULT_THRESHOLDS,
  maxDots = DEFAULT_MAX_DOTS,
  title,
  desc,
  className,
  style,
  margin = { top: 8, right: 12, bottom: 24, left: 12 },
}) {
  const colors = useThemeColors();
  const [hoverIdx, setHoverIdx] = useState(null);

  if (isDev && (!title || typeof title !== 'string')) {
    throw new Error('RiskBeeswarm: `title` prop is required for accessibility (see PRD-69 §3.2.0).');
  }

  const records = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    const cleaned = arr
      .filter((d) => d && typeof d === 'object' && Number.isFinite(d.riskScore))
      .map((d, i) => ({
        id: d.id ?? d.assetId ?? `${i}`,
        label: d.label ?? d.value ?? d.name ?? d.id ?? 'asset',
        riskScore: d.riskScore,
      }))
      .sort((a, b) => b.riskScore - a.riskScore);
    return cleaned;
  }, [data]);

  const overflow = Math.max(0, records.length - maxDots);
  const visible = overflow > 0 ? records.slice(0, maxDots) : records;

  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  // Position positions on data only (NOT on theme) — see PRD-69 §3.4 theme-switch perf note.
  // Re-running d3-beeswarm on every theme change would cause re-layout flicker.
  const positions = useMemo(() => {
    if (visible.length === 0) return [];
    const xScale = scaleLinear({ domain: [0, 100], range: [0, innerW] });
    return beeswarm()
      .data(visible)
      .distributeOn((d) => xScale(d.riskScore))
      .radius(DOT_RADIUS)
      .orientation('horizontal')
      .side('symetric')
      .arrange();
  }, [visible, innerW]);

  const baseline = innerH / 2;
  const axisTicks = [0, 25, 50, 75, 100];

  return (
    <ChartA11y title={title} desc={desc} width={width} height={height} className={className} style={style}>
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        {/* Centerline */}
        <line
          x1={0}
          x2={innerW}
          y1={baseline}
          y2={baseline}
          stroke={colors.border.medium}
          strokeWidth={1}
          strokeDasharray="2 4"
        />

        {/* Dots */}
        {positions.map((p, i) => {
          const bIdx = bandIndex(p.datum.riskScore, thresholds);
          const fill = colorForBand(bIdx, colors);
          const isHover = hoverIdx === i;
          return (
            <circle
              key={p.datum.id}
              cx={p.x}
              cy={baseline + p.y}
              r={isHover ? DOT_RADIUS + 1.5 : DOT_RADIUS}
              fill={fill}
              fillOpacity={hoverIdx == null || isHover ? 0.9 : 0.35}
              stroke={isHover ? colors.text.primary : 'none'}
              strokeWidth={isHover ? 1 : 0}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx((v) => (v === i ? null : v))}
              style={{ cursor: 'pointer', transition: 'r 80ms ease, fill-opacity 80ms ease' }}
            >
              <title>{`${p.datum.label} — risk ${Math.round(p.datum.riskScore)}`}</title>
            </circle>
          );
        })}

        {/* X-axis ticks */}
        {axisTicks.map((t) => {
          const xScale = scaleLinear({ domain: [0, 100], range: [0, innerW] });
          const x = xScale(t);
          return (
            <g key={t} transform={`translate(${x}, ${innerH})`}>
              <line y1={0} y2={4} stroke={colors.text.muted} strokeWidth={1} />
              <text
                y={16}
                textAnchor="middle"
                fontSize={10}
                fill={colors.text.muted}
                fontFamily="inherit"
              >
                {t}
              </text>
            </g>
          );
        })}
      </g>

      {/* Overflow indicator */}
      {overflow > 0 ? (
        <text
          x={width - margin.right}
          y={margin.top + 10}
          textAnchor="end"
          fontSize={11}
          fill={colors.text.secondary}
          fontFamily="inherit"
        >
          +{overflow} more
        </text>
      ) : null}
    </ChartA11y>
  );
}
